/**
 * From `GET /v1/cases/{id}/dashboard` to what the dashboard renders.
 */
import type {
  CaseDashboardModel,
  DashboardHypothesisView,
  DashboardMonthView,
  DashboardNoteView,
  DashboardPanelsView,
  DashboardTileKey,
  DashboardTileView,
  VerdictPoint,
} from '@/components/case-dashboard/dashboard-view'
import { hypothesisPath, investigationPath } from '@/features/analytic-hypotheses/paths'
import { sourceRows } from '@/features/monitor/inbox-rows'
import { toInboxRows } from '@/features/monitor/monitor-rows'
import {
  type AssessmentNoteDto,
  type CaseDashboardDto,
  type DashboardPanelsDto,
  type DashboardTileDto,
  type HypothesisStandingDto,
  SOURCE_KINDS,
  type SourceKind,
  type SourceMonthDto,
} from '@/services/api/case-dashboard'
import {
  HYPOTHESIS_CONFIDENCES,
  HYPOTHESIS_VERDICTS,
  type HypothesisVerdict,
} from '@/types/analytic-hypothesis'

function tile(
  key: DashboardTileKey,
  dto: DashboardTileDto,
  window: readonly string[]
): DashboardTileView {
  return {
    key,
    total: dto.total,
    last30Days: dto.last_30_days,
    sparkline: window.map((month, i) => ({ month, count: dto.sparkline[i] ?? 0 })),
  }
}

function month(dto: SourceMonthDto): DashboardMonthView {
  const sources = Object.fromEntries(
    SOURCE_KINDS.map(kind => [kind, dto.sources[kind] ?? 0])
  ) as Record<SourceKind, number>
  return { month: dto.month, sources, alerts: dto.alerts }
}

function verdictOf(value: string | null): HypothesisVerdict | null {
  return HYPOTHESIS_VERDICTS.find(verdict => verdict === value) ?? null
}

/** A verdict and confidence this build knows, else none (a newer server's word reads as unassessed). */
function pointOf(verdict: string | null, confidence: string | null): VerdictPoint | null {
  const known = verdictOf(verdict)
  const firmness = HYPOTHESIS_CONFIDENCES.find(value => value === confidence)
  return known && firmness ? { verdict: known, confidence: firmness } : null
}

function hypothesis(caseId: string, dto: HypothesisStandingDto): DashboardHypothesisView {
  return {
    id: dto.id,
    title: dto.title,
    href: hypothesisPath(caseId, dto.id),
    point: pointOf(dto.verdict, dto.confidence),
    assessedAt: dto.assessed_at,
    trend: dto.trend,
    previousVerdict: verdictOf(dto.previous_verdict),
  }
}

/** What a note judges: its hypothesis when it has one, else the investigation it was written on. */
function noteAbout(caseId: string, dto: AssessmentNoteDto): DashboardNoteView['about'] {
  if (dto.hypothesis_id && dto.hypothesis_title) {
    const href = hypothesisPath(caseId, dto.hypothesis_id)
    return { kind: 'hypothesis', title: dto.hypothesis_title, href }
  }
  if (dto.investigation_id && dto.investigation_title) {
    const href = investigationPath(caseId, dto.investigation_id)
    return { kind: 'investigation', title: dto.investigation_title, href }
  }
  return null
}

function note(caseId: string, dto: AssessmentNoteDto | null): DashboardNoteView | null {
  if (!dto) return null
  return {
    text: dto.text,
    authorName: dto.author_name,
    writtenAt: dto.written_at,
    about: noteAbout(caseId, dto),
    point: pointOf(dto.verdict, dto.confidence),
  }
}

function panels(caseId: string, dto: DashboardPanelsDto): DashboardPanelsView {
  return {
    hypotheses: dto.hypotheses.map(item => hypothesis(caseId, item)),
    latestNote: note(caseId, dto.latest_note),
    whatsNew: toInboxRows(dto.whats_new),
    watched: dto.watched.map(item => ({
      uid: item.uid,
      label: item.label,
      entityType: item.entity_type,
      lastSeenAt: item.last_seen_at,
    })),
    sources: sourceRows(dto.sources),
    links: { hypotheses: `/cases/${caseId}/investigations`, monitor: `/cases/${caseId}/monitor` },
  }
}

export function toDashboardModel(dto: CaseDashboardDto): CaseDashboardModel {
  return {
    name: dto.name,
    openedAt: dto.opened_at,
    lastIntakeAt: dto.last_intake_at,
    tiles: [
      tile('sources', dto.tiles.sources, dto.window),
      tile('newObjects', dto.tiles.new_objects, dto.window),
      tile('alerts', dto.tiles.alerts, dto.window),
    ],
    sourceKinds: SOURCE_KINDS,
    months: dto.months.map(month),
    panels: panels(dto.case_id, dto.panels),
  }
}
